import { useEffect, useState } from "react";
import api, { apiErr, fmtDate } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, ClipboardList, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Inventories() {
  const [items, setItems] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [magasin, setMagasin] = useState("");
  const [active, setActive] = useState(null);

  const load = () => api.get("/inventories").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/warehouses").then((r) => setWarehouses(r.data)); }, []);

  const create = async () => {
    if (!magasin) { toast.error("Choisir un magasin"); return; }
    try { const { data } = await api.post("/inventories", { magasin_id: magasin }); toast.success("Inventaire créé"); setNewOpen(false); setMagasin(""); load(); setActive(data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer cet inventaire ?")) return; await api.delete(`/inventories/${id}`); toast.success("Supprimé"); load(); };
  const setPhys = (aid, v) => setActive((p) => ({ ...p, lines: p.lines.map((l) => l.article_id === aid ? { ...l, physique: Number(v), ecart: Number(v) - l.theorique } : l) }));
  const setLineComment = (aid, v) => setActive((p) => ({ ...p, lines: p.lines.map((l) => l.article_id === aid ? { ...l, commentaire: v } : l) }));
  const saveCounts = async () => {
    try { const { data } = await api.put(`/inventories/${active.id}`, { commentaire: active.commentaire || "", lines: active.lines.map((l) => ({ article_id: l.article_id, physique: l.physique, commentaire: l.commentaire || "" })) }); setActive(data); toast.success("Comptage enregistré"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const submit = async () => { try { const { data } = await api.post(`/inventories/${active.id}/submit`, {}); setActive(data); toast.success("Soumis pour validation"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const reject = async () => { try { const { data } = await api.post(`/inventories/${active.id}/reject`, { commentaire: active.commentaire || "" }); setActive(data); toast.success("Inventaire rejeté"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const validate = async () => {
    if (!window.confirm("Valider l'inventaire ? Les stocks seront ajustés selon le comptage physique.")) return;
    try { const { data } = await api.post(`/inventories/${active.id}/validate`); setActive(data); toast.success("Inventaire validé, stocks ajustés"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader title="Inventaire" subtitle="Comptage théorique / physique, écarts & validation">
        <Button onClick={() => setNewOpen(true)} data-testid="add-inventory" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouvel inventaire</Button>
      </PageHeader>

      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={ClipboardList} title="Aucun inventaire" subtitle="Lancez un comptage pour un magasin." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>N°</TableHead><TableHead>Magasin</TableHead><TableHead>Date</TableHead><TableHead>Lignes</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((inv) => (
                <TableRow key={inv.id} data-testid={`inventory-row-${inv.id}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => setActive(inv)}>
                  <TableCell className="font-mono text-xs text-slate-500">{inv.numero}</TableCell>
                  <TableCell className="font-semibold">{inv.magasin_name}</TableCell>
                  <TableCell className="text-sm">{fmtDate(inv.date)}</TableCell>
                  <TableCell className="text-slate-600">{inv.lines.length}</TableCell>
                  <TableCell><StatusBadge value={inv.statut} /></TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {inv.statut !== "Validé" && <Button variant="ghost" size="icon" onClick={() => remove(inv.id)} data-testid={`inventory-del-${inv.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-head">Nouvel inventaire</DialogTitle></DialogHeader>
          <Label className="text-xs">Magasin à inventorier</Label>
          <Select value={magasin} onValueChange={setMagasin}><SelectTrigger data-testid="inventory-magasin"><SelectValue placeholder="Choisir un magasin" /></SelectTrigger>
            <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.nom}</SelectItem>)}</SelectContent></Select>
          <DialogFooter><Button variant="outline" onClick={() => setNewOpen(false)}>Annuler</Button><Button onClick={create} data-testid="inventory-create" className="bg-brand-600 hover:bg-brand-700 text-white">Créer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="inventory-count">
          {active && (
            <>
              <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><ClipboardList className="h-5 w-5 text-brand-600" />{active.numero} · {active.magasin_name} <StatusBadge value={active.statut} /></DialogTitle></DialogHeader>
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="text-left p-2">Article</th><th className="p-2">Théorique</th><th className="p-2">Physique</th><th className="p-2">Écart</th><th className="p-2 text-left">Commentaire (écart)</th></tr></thead>
                  <tbody>
                    {active.lines.map((l) => (
                      <tr key={l.article_id} className="border-t">
                        <td className="p-2">{l.article_name} <span className="text-slate-400 text-xs">{l.unite}</span></td>
                        <td className="p-2 text-center font-mono text-xs">{l.theorique}</td>
                        <td className="p-2 text-center">{active.statut === "Validé" ? <span className="font-mono text-xs">{l.physique}</span> : <Input type="number" value={l.physique} onChange={(e) => setPhys(l.article_id, e.target.value)} data-testid={`inv-phys-${l.article_id}`} className="h-8 w-24 mx-auto" />}</td>
                        <td className={`p-2 text-center font-mono text-xs font-semibold ${l.ecart > 0 ? "text-emerald-600" : l.ecart < 0 ? "text-rose-600" : "text-slate-400"}`}>{l.ecart > 0 ? "+" : ""}{l.ecart}</td>
                        <td className="p-2">{active.statut === "Validé" ? <span className="text-xs text-slate-500">{l.commentaire || "—"}</span> : <Input value={l.commentaire || ""} onChange={(e) => setLineComment(l.article_id, e.target.value)} data-testid={`inv-comment-${l.article_id}`} placeholder={l.ecart !== 0 ? "Motif de l'écart…" : ""} className="h-8" />}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div><Label className="text-xs">Commentaire général de l'inventaire</Label><Input value={active.commentaire || ""} onChange={(e) => setActive({ ...active, commentaire: e.target.value })} data-testid="inv-general-comment" disabled={active.statut === "Validé"} className="mt-1" /></div>
              {active.statut !== "Validé" && active.statut !== "Rejeté" && (
                <DialogFooter className="flex-wrap gap-2">
                  <Button variant="outline" onClick={saveCounts} data-testid="inventory-save">Enregistrer le comptage</Button>
                  {active.statut === "Brouillon" && <Button variant="outline" onClick={submit} data-testid="inventory-submit">Soumettre</Button>}
                  {active.statut === "En attente" && <Button variant="outline" onClick={reject} data-testid="inventory-reject" className="text-amber-600">Rejeter</Button>}
                  <Button onClick={validate} data-testid="inventory-validate" className="bg-brand-600 hover:bg-brand-700 text-white"><CheckCircle2 className="h-4 w-4 mr-1.5" />Valider &amp; ajuster</Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
